/**
 * CsvViewerModal — opens a CSV inside the same comment-pin / markup shell as PDFs and images.
 *
 * Treats the CSV as a single virtual "page 1", so the existing `pdfAnnotations` and
 * `documentMarkup` Firestore collections are reused with no schema changes. Adds a
 * tabular `Highlight` mode (cell / row / column) backed by the new `csvCellHighlights`
 * collection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import SharedWholeProjectNote from '../pm/SharedWholeProjectNote';
import {
  subscribeToMarkupForDocument,
  createMarkupStroke,
  deleteMarkupStroke,
} from '../../utils/documentMarkupService';
import { subscribeCellHighlightsForDocument } from '../../utils/csvCellHighlightService';
import type {
  DocumentMarkupStroke,
  DocumentMarkupTool,
  CsvCellHighlight,
} from '../../types';
import { AnnotationLayer } from '../pdf/AnnotationLayer';
import { fileCommentNotifiesWithoutMention } from '../../utils/untaggedCommentConfirm';
import { PdfCommentsPopover } from '../pdf/PdfCommentsPopover';
import { ViewerToolbar, buildDownloadFilename } from '../pdf/ViewerToolbar';
import {
  ViewerModeBar,
  type ViewerInteractionMode,
  type CsvHighlightSubTool,
} from '../pdf/ViewerModeBar';
import { MarkupLayer, type MarkupCommitPayload } from '../pdf/MarkupLayer';
import { useMarkupStrokeWidth } from '../pdf/markupStrokeWidth';
import { fetchAndParseCsv, type ParsedCsv } from '../../utils/csvParse';
import { useCsvHighlights } from './CsvCellHighlightLayer';
import { useAuth } from '../../utils/AuthContext';
import { usePdfAnnotations } from '../../hooks/usePdfAnnotations';
import { markAnnotationAsRead } from '../../utils/pdfAnnotationService';
import '../pdf/PdfViewerModal.css';
import './CsvViewerModal.css';

const CSV_PAGE = 1;
const DEFAULT_HIGHLIGHT_COLOR = '#fde68a';
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.25;
const ZOOM_STEP = 0.1;
const VISIBLE_OVERSCAN = 8;

export interface CsvViewerModalProps {
  url: string;
  title: string;
  documentId: string;
  leadId: string;
  leadName?: string;
  projectId?: string;
  dealId?: string;
  taskId?: string;
  initialAnnotationId?: string;
  /** This document is the one whole-project file, opened from any structure track. */
  sharedAcrossStructures?: boolean;
  /** Timber CSV: keep cross-section inches as written in the file. */
  trueDimensions?: boolean;
  onClose: () => void;
}

export default function CsvViewerModal({
  url,
  title,
  documentId,
  leadId,
  projectId,
  taskId,
  initialAnnotationId,
  sharedAcrossStructures = false,
  trueDimensions = false,
  onClose,
}: CsvViewerModalProps) {
  const { user } = useAuth();
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const [markupStrokes, setMarkupStrokes] = useState<DocumentMarkupStroke[]>([]);
  const [highlights, setHighlights] = useState<CsvCellHighlight[]>([]);

  const [viewerMode, setViewerMode] = useState<ViewerInteractionMode>('comment');
  const [drawTool, setDrawTool] = useState<DocumentMarkupTool>('pen');
  const [drawColor, setDrawColor] = useState('#3b82f6');
  const { strokeWidth: drawStrokeWidth, setStrokeWidth: setDrawStrokeWidth } = useMarkupStrokeWidth(drawTool);
  const [highlightTool, setHighlightTool] = useState<CsvHighlightSubTool>('cell');
  const [highlightColor, setHighlightColor] = useState(DEFAULT_HIGHLIGHT_COLOR);
  const [scrollY, setScrollY] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  // Comment pins reuse the PDF model — single virtual page so floats stay valid.
  const {
    annotations,
    loadingAnnotations,
    focusedAnnotationId,
    setFocusedAnnotationId,
    loadAnnotations,
    handleAddAnnotation,
    handleAddReply,
  } = usePdfAnnotations({
    documentId,
    documentUrl: url,
    leadId,
    projectId,
    taskId,
    user,
    initialAnnotationId,
    annotationPageFilter: CSV_PAGE,
  });

  // Markup strokes (pen / highlighter) — same Firestore collection as PDFs and images.
  useEffect(() => {
    const unsub = subscribeToMarkupForDocument(documentId, url, list =>
      setMarkupStrokes(list.filter(s => s.pageNumber === CSV_PAGE)),
    );
    return () => unsub();
  }, [documentId, url]);

  // Tabular cell / row / column highlights.
  useEffect(() => {
    const unsub = subscribeCellHighlightsForDocument(documentId, url, setHighlights);
    return () => unsub();
  }, [documentId, url]);

  // Load + parse the CSV.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setParsed(null);
    fetchAndParseCsv(url, { trueDimensions })
      .then(p => {
        if (cancelled) return;
        setParsed(p);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Could not load this CSV.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url, trueDimensions]);

  const EMPTY_HEADERS = useMemo(() => [] as string[], []);
  const EMPTY_ROWS = useMemo(() => [] as string[][], []);
  const headers = parsed?.headers ?? EMPTY_HEADERS;
  const rows = parsed?.rows ?? EMPTY_ROWS;

  const csvHighlights = useCsvHighlights({
    documentId,
    documentUrl: url,
    leadId,
    projectId,
    highlights,
    enabled: viewerMode === 'highlight',
    scope: highlightTool,
    color: highlightColor,
    headers,
    currentUser: user,
  });

  const onMarkupCommit = useCallback(
    async (payload: MarkupCommitPayload) => {
      if (!user?.id) return;
      try {
        await createMarkupStroke({
          documentId,
          documentUrl: url,
          leadId,
          ...(projectId ? { projectId } : {}),
          pageNumber: payload.pageNumber,
          tool: payload.tool,
          color: payload.color,
          strokeWidth: payload.strokeWidth,
          points: payload.points,
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
          createdAt: new Date(),
        });
      } catch (err) {
        console.error('Error saving markup:', err);
      }
    },
    [documentId, url, leadId, projectId, user],
  );

  const onMarkupDelete = useCallback(async (strokeId: string) => {
    try {
      await deleteMarkupStroke(strokeId);
    } catch (err) {
      console.error('Error deleting markup:', err);
    }
  }, []);

  const handleSelectComment = useCallback(
    (annotationId: string, page: number) => {
      if (page !== CSV_PAGE) return;
      setFocusedAnnotationId(annotationId);
      if (user?.id) {
        void markAnnotationAsRead(annotationId, user.id);
      }
    },
    [setFocusedAnnotationId, user?.id],
  );

  // Track scroll + viewport so we can virtualize body rows; CSVs in this app can be large.
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const onScroll = () => setScrollY(el.scrollTop);
    setScrollY(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [parsed]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight));
    setViewportHeight(el.clientHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [parsed]);

  // Wheel zoom (Ctrl/Cmd + wheel) to match the PDF/image viewer ergonomics.
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = -e.deltaY * 0.0015;
      setZoom(z => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z + delta)));
    };
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true });
  }, []);

  const rowHeightPx = Math.round(28 * zoom);
  const headerHeightPx = Math.round(34 * zoom);
  const fontSize = `${(0.85 * zoom).toFixed(2)}rem`;

  const totalRows = rows.length;
  const visibleStart = Math.max(0, Math.floor(scrollY / rowHeightPx) - VISIBLE_OVERSCAN);
  const visibleCount = Math.min(
    totalRows - visibleStart,
    Math.ceil((viewportHeight || 600) / rowHeightPx) + VISIBLE_OVERSCAN * 2,
  );
  const padTop = visibleStart * rowHeightPx;
  const padBottom = Math.max(0, (totalRows - (visibleStart + visibleCount)) * rowHeightPx);

  const visibleRows = useMemo(() => {
    if (totalRows === 0) return [];
    return rows.slice(visibleStart, visibleStart + visibleCount).map((row, i) => ({
      row,
      rowIndex: visibleStart + i,
    }));
  }, [rows, totalRows, visibleStart, visibleCount]);

  const annotationInteractive = viewerMode === 'comment';
  const markupInteractive = viewerMode === 'draw';
  const highlightInteractive = viewerMode === 'highlight';
  const tableInteractive = !markupInteractive; // draw mode owns the SVG; everything else needs cell clicks

  const handleCellClick = (row: number, col: number) => {
    if (!highlightInteractive) return;
    void csvHighlights.onCellClick(row, col);
  };
  const handleHeaderClick = (col: number) => {
    if (!highlightInteractive || highlightTool !== 'column') return;
    void csvHighlights.onHeaderClick(col);
  };

  const numericCols = parsed?.numericCols ?? new Set<number>();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (commentsOpen) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [commentsOpen, onClose]);

  return (
    <div className="pdf-viewer-modal-overlay" onClick={onClose}>
      <div className="pdf-viewer-modal csv-viewer-modal" onClick={e => e.stopPropagation()}>
        <header className="pdf-viewer-header">
          {trueDimensions ? (
            <span
              className="csv-viewer-true-dim"
              title="True timber dimensions. Cross-section stays as written in the CSV (7.5 stays 7.5)."
            >
              True dimensions
            </span>
          ) : null}
          <h2 className="pdf-viewer-title">{title}</h2>
          {parsed && (
            <span className="csv-viewer-meta">
              {rows.length.toLocaleString()} row{rows.length === 1 ? '' : 's'} ·{' '}
              {headers.length} column{headers.length === 1 ? '' : 's'}
            </span>
          )}
          <div className="pdf-viewer-actions">
            <button
              type="button"
              className="pdf-viewer-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </header>
        {sharedAcrossStructures ? <SharedWholeProjectNote /> : null}

        <div className="pdf-viewer-body">
          <div className="pdf-viewer-main">
            <ViewerModeBar
              mode={viewerMode}
              onModeChange={setViewerMode}
              drawTool={drawTool}
              onDrawToolChange={setDrawTool}
              drawColor={drawColor}
              onDrawColorChange={setDrawColor}
              drawStrokeWidth={drawStrokeWidth}
              onDrawStrokeWidthChange={setDrawStrokeWidth}
              csvVariant
              hideMeasure
              showShapeTools={false}
              highlightTool={highlightTool}
              onHighlightToolChange={setHighlightTool}
              highlightColor={highlightColor}
              onHighlightColorChange={setHighlightColor}
            />

            {markupInteractive ? (
              <div className="csv-viewer-mode-hint">
                Drawing mode — pin comments, scrolling, and cell highlighting are paused while you sketch.
              </div>
            ) : null}
            {parsed?.parseErrors && parsed.parseErrors.length > 0 ? (
              <div className="csv-viewer-warning" title={parsed.parseErrors.join('\n')}>
                Parsed with warnings ({parsed.parseErrors.length}). Hover for details.
              </div>
            ) : null}

            <div className="pdf-viewer-canvas-wrap" ref={canvasWrapRef}>
              {loading && (
                <div className="pdf-viewer-loading">
                  <span>Loading CSV…</span>
                </div>
              )}
              {loadError && !loading && (
                <div className="pdf-viewer-error">
                  <span>{loadError}</span>
                </div>
              )}
              {!loading && !loadError && parsed && (
                <div className="csv-viewer-stage">
                  <div
                    className={`csv-viewer-table-scroll${markupInteractive ? ' csv-viewer-table-scroll--locked' : ''}`}
                    ref={tableScrollRef}
                  >
                    <div className="csv-viewer-content">
                    <table
                      className={`csv-viewer-table${highlightInteractive ? ' csv-viewer-table--highlight-mode' : ''}`}
                      style={{ fontSize }}
                    >
                      <thead>
                        <tr style={{ height: headerHeightPx }}>
                          <th
                            className="csv-viewer-rownum-cell csv-viewer-rownum-cell--header"
                            aria-hidden
                          />
                          {headers.map((h, c) => {
                            const bg = csvHighlights.headerBg(c);
                            const numeric = numericCols.has(c);
                            const isClickable =
                              highlightInteractive && highlightTool === 'column';
                            return (
                              <th
                                key={`h-${c}`}
                                className={`csv-viewer-cell csv-viewer-cell--header${
                                  numeric ? ' csv-viewer-cell--num' : ''
                                }${isClickable ? ' csv-viewer-cell--clickable' : ''}`}
                                style={bg ? { background: bg } : undefined}
                                onClick={isClickable ? () => handleHeaderClick(c) : undefined}
                              >
                                {h}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {padTop > 0 && (
                          <tr aria-hidden style={{ height: padTop }}>
                            <td colSpan={headers.length + 1} style={{ padding: 0, border: 0 }} />
                          </tr>
                        )}
                        {visibleRows.map(({ row, rowIndex }) => (
                          <tr key={`r-${rowIndex}`} style={{ height: rowHeightPx }}>
                            <td
                              className="csv-viewer-rownum-cell"
                              aria-hidden
                            >
                              {rowIndex + 1}
                            </td>
                            {row.map((value, c) => {
                              const bg = csvHighlights.cellBg(rowIndex, c);
                              const numeric = numericCols.has(c);
                              const isClickable =
                                highlightInteractive &&
                                tableInteractive &&
                                highlightTool !== 'column';
                              const onClick = (() => {
                                if (highlightInteractive && tableInteractive) {
                                  if (highlightTool === 'column') return undefined;
                                  return () => handleCellClick(rowIndex, c);
                                }
                                return undefined;
                              })();
                              return (
                                <td
                                  key={`c-${rowIndex}-${c}`}
                                  className={`csv-viewer-cell${
                                    numeric ? ' csv-viewer-cell--num' : ''
                                  }${isClickable ? ' csv-viewer-cell--clickable' : ''}`}
                                  style={bg ? { background: bg } : undefined}
                                  onClick={onClick}
                                  title={value}
                                >
                                  {value}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                        {padBottom > 0 && (
                          <tr aria-hidden style={{ height: padBottom }}>
                            <td colSpan={headers.length + 1} style={{ padding: 0, border: 0 }} />
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div
                      className="csv-viewer-overlay"
                      style={{
                        pointerEvents: markupInteractive ? 'auto' : 'none',
                        zIndex: markupInteractive ? 6 : 4,
                      }}
                    >
                      <MarkupLayer
                        pageNumber={CSV_PAGE}
                        scale={zoom}
                        strokes={markupStrokes}
                        activeTool={drawTool}
                        color={drawColor}
                        baseStrokeWidth={drawStrokeWidth}
                        enabled={markupInteractive}
                        currentUserId={user?.id}
                        onCommitStroke={onMarkupCommit}
                        onDeleteStroke={onMarkupDelete}
                      />
                    </div>
                    <div
                      className="csv-viewer-overlay"
                      style={{
                        pointerEvents: annotationInteractive ? 'auto' : 'none',
                        zIndex: annotationInteractive ? 7 : 5,
                      }}
                    >
                      <AnnotationLayer
                        pageNumber={CSV_PAGE}
                        scale={zoom}
                        annotations={annotations}
                        loading={loadingAnnotations}
                        interactive={annotationInteractive}
                        onAddAnnotation={handleAddAnnotation}
                        onAddReply={handleAddReply}
                        onDeleteAnnotation={loadAnnotations}
                        onAnnotationMoved={loadAnnotations}
                        currentUserId={user?.id}
                        focusAnnotationId={focusedAnnotationId}
                        onAnnotationViewed={loadAnnotations}
                        notifiesWithoutMention={fileCommentNotifiesWithoutMention(leadId, documentId)}
                      />
                    </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <ViewerToolbar
              displayScale={zoom}
              fitMode={false}
              commentsOpen={commentsOpen}
              annotationCount={annotations.length}
              onCommentsOpen={() => setCommentsOpen(true)}
              onZoomOut={() => setZoom(z => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
              onZoomIn={() => setZoom(z => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
              onZoomToFit={() => setZoom(1)}
              minScale={ZOOM_MIN}
              maxScale={ZOOM_MAX}
              downloadUrl={url}
              downloadFilename={buildDownloadFilename(title, '.csv')}
            />
          </div>
        </div>
      </div>

      {commentsOpen && (
        <PdfCommentsPopover
          annotations={annotations}
          currentPageNumber={CSV_PAGE}
          currentUserId={user?.id}
          onSelectComment={handleSelectComment}
          onClose={() => setCommentsOpen(false)}
        />
      )}
    </div>
  );
}
